import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { ApiService } from '../../../services/api.service';

export interface ExternalCommentRow {
  id?: number;
  external_comment?: number;
  name: string;
  description: string;
  document_number: string;
  document_date: string;
  upload_date: string;
  file_url?: string;
  selectedFile?: File | null;
  uploading?: boolean;
  editing?: boolean;
}

@Component({
  selector: 'app-upload-comments-from-external-agencies',
  standalone: false,
  templateUrl: './upload-comments-from-external-agencies.component.html',
  styleUrl: './upload-comments-from-external-agencies.component.scss'
})
export class UploadCommentsFromExternalAgenciesComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() stepDetails: any;
  @Input() isEditable: boolean = false;
  @Input() schemeType: any;
  @Input() proposalName: any;
  @Input() step: any;
  @Input() id: any;
  @Output() routeConfig: EventEmitter<any> = new EventEmitter();

  rows: ExternalCommentRow[] = [];
  externalCommentId: number | null = null;
  today: string = new Date().toISOString().split('T')[0];

  payload!: { proposal_id: any; proposal_name: any };
  private subscriptions: Subscription = new Subscription();

  constructor(
    private api: ApiService,
    private messageService: MessageService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    console.log("schemeType", this.schemeType);
    this.route.queryParams.subscribe((params: any) => {
      this.payload = {
        proposal_id: params['id'],
        proposal_name: params['name'],
      };
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['id'] || changes['step']) && this.id && this.payload?.proposal_id) {
      if (!changes['id']?.firstChange && !changes['step']?.firstChange) {
        if (changes['id']?.currentValue !== changes['id']?.previousValue) {
          this.loadExternalComments();
        }
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.id && this.payload?.proposal_id) {
      this.loadExternalComments();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadExternalComments(): void {
    if (!this.stepDetails?.id || !this.payload?.proposal_id) return;

    const url = `/make1/external/comments/?proposal_step=${this.stepDetails.id}&proposal_step__proposal=${this.payload.proposal_id}`;
    const sub = this.api.getAPI(url).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : [res];
        if (data.length > 0) {
          this.externalCommentId = data[0].id;
          const files: any[] = data[0]?.external_files || [];
          this.rows = files.map((f: any) => ({
              id: f.id,
              external_comment: f.external_comment,
              name: f.name || '',
              description: f.description || '',
              document_number: f.document_number || '',
              document_date: f.document_date || '',
              upload_date: f.uploaded_at?.split('T')[0] || this.today,
              file_url: f.file || '',
              selectedFile: null,
              uploading: false,
              editing: false
           }));
        }
        if (this.rows.length === 0) {
          this.addEmptyRow();
        }
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load external comments.' });
        this.addEmptyRow();
      }
    });
    this.subscriptions.add(sub);
  }

  addEmptyRow(): void {
    this.rows.push({
      name: '',
      description: '',
      document_number: '',
      document_date: '',
      upload_date: this.today,
      selectedFile: null,
      uploading: false,
      editing: false
    });
  }

  incrementRows(): void {
    this.addEmptyRow();
  }

  decrementRows(): void {
    if (this.rows.length <= 1) return;
    const last = this.rows[this.rows.length - 1];
    if (!last.id) {
      this.rows.pop();
    }
  }

  onFileSelected(event: Event, row: ExternalCommentRow): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      row.selectedFile = input.files[0];
      row.upload_date = this.today;
    }
  }

  uploadRow(row: ExternalCommentRow, index: number): void {
    if (!row.selectedFile) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please select a file to upload.' });
      return;
    }
    if (!row.name.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Agency name is required.' });
      return;
    }
    if (!this.externalCommentId) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'External comment ID not found.' });
      return;
    }

    row.uploading = true;

    const formData = new FormData();
    formData.append('external_comment', String(this.externalCommentId));
    formData.append('name', row.name);
    formData.append('description', row.description);
    formData.append('document_number', row.document_number);
    formData.append('document_date', row.document_date);
    formData.append('file', row.selectedFile);

    this.api.postAPI(`/make1/external/comments/files/`, formData).subscribe({
      next: (res: any) => {
        row.id = res.id;
        row.external_comment = res.external_comment;
        row.file_url = res.file;
        row.upload_date = res.upload_date || res.created_at?.split('T')[0] || this.today;
        row.selectedFile = null;
        row.uploading = false;
        row.editing = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Document uploaded successfully.' });

        // Add new empty row if this was the last row
        const hasEmptyRow = this.rows.some(r => !r.id);
        if (!hasEmptyRow) {
          this.addEmptyRow();
        }
      },
      error: () => {
        row.uploading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to upload document.' });
      }
    });
  }

  editRow(row: ExternalCommentRow, index: number): void {
    row.editing = true;
  }

  deleteRow(row: ExternalCommentRow, index: number): void {
    if (!row.id) {
      this.rows.splice(index, 1);
      if (this.rows.length === 0) this.addEmptyRow();
      return;
    }

    this.api.deleteAPI(`/make1/external/comments/files/${row.id}/`).subscribe({
      next: () => {
        this.rows.splice(index, 1);
        if (this.rows.length === 0) this.addEmptyRow();
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Document deleted successfully.' });
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete document.' });
      }
    });
  }

 downloadDocument(row: ExternalCommentRow): void {

  if (!row.file_url) return;

  const baseUrl = 'https://iima-api.ilizien-projects-cdf.in';

  const link = document.createElement('a');
  link.href = baseUrl + row.file_url;
  link.target = '_blank';
  link.download = row.name || 'document';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

}
}